import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { CheckCircle2, FileText, Info, Loader2 } from "lucide-react";

import StatusBadge from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import { API } from "../../constant/api";
import { useBusiness } from "../../hooks/use-business";
import { DOCS, useDocumentUploads } from "../../hooks/use-document-uploads";
import useAuthAxios from "../../utils/baseAxios";
import { errorMessage } from "../../utils/errorMessage";
import DocumentUploadField from "./components/DocumentUploadField";
import ReadOnlyField from "./components/ReadOnlyField";
import SettingsSection from "./components/SettingsSection";

const Documents = () => {
  const axiosInstance = useAuthAxios();
  const queryClient = useQueryClient();
  const { business, businessId, isRegistered, isVerified, canEdit, isLoading } =
    useBusiness();

  const [preview, setPreview] = useState<{ label: string; url: string } | null>(
    null
  );
  const [bnNumber, setBnNumber] = useState("");

  const {
    docs,
    upload,
    clear,
    allUploaded,
    anyUploading,
    uploadedCount,
    documents: uploadedDocuments,
    reset,
  } = useDocumentUploads();

  const submit = useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.put(
        API.submitBusinessDocuments(businessId!),
        {
          bnNumber: bnNumber.trim(),
          identity: uploadedDocuments.identity,
          proofOfAddress: uploadedDocuments.proofOfAddress,
          cac: uploadedDocuments.cac,
        }
      );
      return data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || "Documents submitted for review");
      queryClient.invalidateQueries({ queryKey: ["get-business"] });
      setBnNumber("");
      reset();
    },
    onError: (e: any) =>
      toast.error(errorMessage(e?.response?.data?.message || e?.message)),
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-[180px] w-full rounded-xl" />
        <Skeleton className="h-[320px] w-full rounded-xl" />
      </div>
    );
  }

  const existing = business?.documents ?? {};
  const canSubmit =
    !!businessId && !!bnNumber.trim() && allUploaded && !anyUploading;

  // A rejected business is resubmitting; a starter one is upgrading for the
  // first time. Same request either way, different framing.
  const isResubmitting = isRegistered;

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Verification status"
        description="Set by our compliance team after reviewing your submission."
        action={<StatusBadge status={business?.status ?? "PENDING"} />}
      >
        <div className="grid grid-cols-1 gap-4 mds:grid-cols-2">
          <ReadOnlyField
            label="Business type"
            value={isRegistered ? "Registered business" : "Starter business"}
          />
          <ReadOnlyField label="RN / BN number" value={business?.bnNumber} />
        </div>
      </SettingsSection>

      {/* Documents already on file. Always shown when there are any. */}
      {isRegistered && (
        <SettingsSection
          title="Submitted documents"
          description={
            isVerified
              ? "Verified and on file. Contact support to replace any of these."
              : "Currently under review."
          }
        >
          <div className="grid grid-cols-1 gap-4 mds:grid-cols-3">
            {DOCS.map((doc) => {
              const url: string | undefined = existing?.[doc.field];
              return (
                <div key={doc.key} className="space-y-2">
                  <p className="text-[13px] font-semibold text-foreground">
                    {doc.label}
                  </p>
                  {url ? (
                    <button
                      type="button"
                      onClick={() => setPreview({ label: doc.label, url })}
                      className="block w-full overflow-hidden rounded-xl border bg-muted transition-opacity hover:opacity-90"
                    >
                      <img
                        src={url}
                        alt={doc.label}
                        className="h-[140px] w-full object-contain"
                      />
                    </button>
                  ) : (
                    <div className="flex h-[140px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-muted/50 px-3 text-center">
                      <FileText className="size-5 text-muted-foreground" />
                      <p className="text-[12px] font-medium text-muted-foreground">
                        Not submitted
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </SettingsSection>
      )}

      {/* Upload form — open until the business is both registered and verified. */}
      {canEdit ? (
        <SettingsSection
          title={
            isResubmitting
              ? "Resubmit documents"
              : "Upgrade to a registered business"
          }
          description={
            isResubmitting
              ? "Upload corrected documents and resubmit for review."
              : "Submit your RN number and registration documents to be verified as a registered business."
          }
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (canSubmit && !submit.isPending) submit.mutate();
            }}
            className="space-y-6"
          >
            <div className="space-y-2">
              <Label htmlFor="bn-number">RN number</Label>
              <Input
                id="bn-number"
                type="tel"
                inputMode="numeric"
                value={bnNumber}
                onChange={(e) => setBnNumber(e.target.value)}
                placeholder={business?.bnNumber || "0123456789"}
              />
            </div>

            {DOCS.map((doc) => (
              <DocumentUploadField
                key={doc.key}
                label={doc.label}
                hint={doc.hint}
                file={docs[doc.key].file}
                status={docs[doc.key].status}
                error={docs[doc.key].error}
                onSelect={(file) => upload(doc.key, doc.documentType, file)}
                onClear={() => clear(doc.key)}
                onRetry={() => {
                  const file = docs[doc.key].file;
                  if (file) upload(doc.key, doc.documentType, file);
                }}
              />
            ))}

            <div className="flex items-center gap-2 rounded-xl bg-muted/60 px-3 py-2.5">
              <CheckCircle2
                className={
                  allUploaded
                    ? "size-4 shrink-0 text-success"
                    : "size-4 shrink-0 text-muted-foreground"
                }
              />
              <p className="text-[12px] font-medium text-muted-foreground">
                {uploadedCount} of {DOCS.length} documents uploaded
                {!bnNumber.trim() && " • RN number required"}
              </p>
            </div>

            <Button
              type="submit"
              disabled={!canSubmit || submit.isPending}
              className="h-[52px] w-full rounded-xl text-[15px] font-semibold"
            >
              {submit.isPending ? (
                <Loader2 className="size-5 animate-spin" />
              ) : isResubmitting ? (
                "Resubmit for review"
              ) : (
                "Submit for verification"
              )}
            </Button>
          </form>
        </SettingsSection>
      ) : (
        <div className="flex items-start gap-3 rounded-xl bg-accent p-4">
          <Info className="mt-0.5 size-4 shrink-0 text-primary" />
          <p className="text-[13px] font-medium text-primary">
            Your business is verified. Contact support to change your
            registration details or replace a document.
          </p>
        </div>
      )}

      <Dialog
        open={!!preview}
        onOpenChange={(next) => !next && setPreview(null)}
      >
        <DialogContent className="font-manrope mds:max-w-[640px]">
          <DialogHeader>
            <DialogTitle className="font-sora">{preview?.label}</DialogTitle>
          </DialogHeader>
          {preview && (
            <img
              src={preview.url}
              alt={preview.label}
              className="max-h-[70vh] w-full rounded-xl object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Documents;
